import React, { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useHistory } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSync, faEye } from "@fortawesome/free-solid-svg-icons"
import { fetchInventaireArticleUsages } from "../../../store/actions/atelierInventaireActions"
import { COLORS } from "../../../utilities/Constants"
import { generalStyles as gs } from "../../styles/styles"
import { tableStyles } from "../../styles/tableStyles"
import Loader from "../../basics/Loader"

const US_LIMIT = 25

const UsageRow = ({ usage, index, onAtelier, onCommande }) => {
	const [hover, setHover] = useState(false)
	const bg = hover ? tableStyles.rowHover : tableStyles.rowBg(index)
	const q = usage.quantite
	const qColor = q > 0 ? COLORS.success : q < 0 ? COLORS.danger : COLORS.text
	const hasAtelier = usage.atelier > 0
	const hasCommande = usage.commande && usage.commande.id > 0
	return (
		<tr
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
			style={{
				backgroundColor: bg,
				transition: "background-color 0.12s",
			}}
		>
			<td style={{ ...s.td, color: COLORS.textMuted }}>{usage.date}</td>
			<td style={s.td}>{usage.categorieNom || "—"}</td>
			<td style={{ ...s.td, fontWeight: 600 }}>
				{usage.articleNom || "—"}
			</td>
			<td style={{ ...s.td, color: COLORS.textMuted }}>
				{usage.label || "—"}
			</td>
			<td style={{ ...s.tdRight, color: qColor, fontWeight: 600 }}>
				{q > 0 ? `+${q}` : q}
			</td>
			<td style={s.tdCenter}>
				{hasAtelier ? (
					<button
						type="button"
						style={gs.iconBtn(COLORS.textMuted)}
						onClick={() => onAtelier(usage.atelier)}
						title="Voir la fiche atelier"
					>
						<FontAwesomeIcon icon={faEye} />
					</button>
				) : hasCommande ? (
					<button
						type="button"
						style={gs.iconBtn(COLORS.textMuted)}
						onClick={() => onCommande(usage.commande)}
						title="Voir la commande"
					>
						<FontAwesomeIcon icon={faEye} />
					</button>
				) : (
					"—"
				)}
			</td>
		</tr>
	)
}

// Onglet "Utilisations" de l'inventaire V2 (boss) : historique des mouvements
// d'articles, en lecture seule, avec filtres + endless scroll. Réutilise
// l'infra Redux existante (fetchInventaireArticleUsages).
const UtilisationsV2 = ({
	onShowCommande,
	initialCat = 0,
	initialArticle = 0,
	onFilterChange,
	skipInitialFetch = false,
}) => {
	const dispatch = useDispatch()
	const history = useHistory()
	const categories = useSelector((s) => s.atelierInventaire.categories)
	const allArticles = useSelector((s) => s.atelierInventaire.allArticles)
	const {
		articleUsages,
		fetchArticleUsages,
		articleUsagesHasMore,
		articleUsagesLoadingMore,
	} = useSelector((s) => s.atelierInventaire)

	const [usCat, setUsCat] = useState(initialCat)
	const [usArticle, setUsArticle] = useState(initialArticle)
	const sentinelRef = useRef(null)
	const mountRef = useRef(true)

	const buildKwargs = (offset) => {
		const k = { offset, limit: US_LIMIT }
		if (usCat > 0) k.categorie = usCat
		if (usArticle > 0) k.article = usArticle
		return k
	}

	useEffect(() => {
		if (mountRef.current) {
			mountRef.current = false
			if (skipInitialFetch) return
		}
		dispatch(fetchInventaireArticleUsages(buildKwargs(0)))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, usCat, usArticle])

	// Endless scroll.
	useEffect(() => {
		const el = sentinelRef.current
		if (!el) return
		const obs = new IntersectionObserver(
			(entries) => {
				// Pas d'append pendant le rechargement page 1 (changement de
				// filtre) : l'offset serait celui de l'ancienne liste.
				if (
					entries[0].isIntersecting &&
					articleUsagesHasMore &&
					!articleUsagesLoadingMore &&
					!fetchArticleUsages.loading
				) {
					dispatch(
						fetchInventaireArticleUsages(
							buildKwargs(articleUsages.length),
						),
					)
				}
			},
			{ rootMargin: "200px" },
		)
		obs.observe(el)
		return () => obs.disconnect()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		dispatch,
		articleUsages.length,
		articleUsagesHasMore,
		articleUsagesLoadingMore,
		fetchArticleUsages.loading,
		usCat,
		usArticle,
	])

	const sortedCats = [...(categories || [])].sort((a, b) =>
		(a.nom || "").localeCompare(b.nom || "", "fr"),
	)
	// Articles du filtre : tous, restreints à la catégorie sélectionnée.
	const filterArticles = (allArticles || [])
		.filter((a) => usCat === 0 || a.categorie === usCat)
		.sort((a, b) => (a.nom || "").localeCompare(b.nom || "", "fr"))

	const changeCat = (v) => {
		setUsCat(v)
		setUsArticle(0)
		if (onFilterChange) onFilterChange({ cat: v, article: 0 })
	}

	const onAtelier = (id) => history.push(`/atelier/fiche/${id}`)
	const onCommande = (commande) => onShowCommande && onShowCommande(commande)

	const loading = fetchArticleUsages.loading && articleUsages.length === 0

	return (
		<>
			<div style={s.toolbar}>
				<div style={s.field}>
					<span style={s.label}>Catégorie</span>
					<select
						style={s.input}
						value={usCat}
						onChange={(e) => changeCat(parseInt(e.target.value))}
					>
						<option value={0}>Toutes</option>
						{sortedCats.map((c) => (
							<option key={c.id} value={c.id}>
								{c.nom}
							</option>
						))}
					</select>
				</div>
				<div style={s.field}>
					<span style={s.label}>Article</span>
					<select
						style={s.input}
						value={usArticle}
						onChange={(e) => {
							const v = parseInt(e.target.value)
							setUsArticle(v)
							if (onFilterChange)
								onFilterChange({ cat: usCat, article: v })
						}}
					>
						<option value={0}>Tous</option>
						{filterArticles.map((a) => (
							<option key={a.id} value={a.id}>
								{a.nom}
							</option>
						))}
					</select>
				</div>
			</div>

			<div style={tableStyles.container}>
				{loading ? (
					<Loader
						variant="ring"
						message="Chargement de l'historique…"
					/>
				) : articleUsages.length === 0 ? (
					<div style={tableStyles.empty}>Aucune utilisation.</div>
				) : (
					<table style={tableStyles.table}>
						<thead>
							<tr>
								<th style={tableStyles.th}>Date</th>
								<th style={tableStyles.th}>Catégorie</th>
								<th style={tableStyles.th}>Article</th>
								<th style={tableStyles.th}>Utilisation</th>
								<th style={tableStyles.thRight}>Quantité</th>
								<th style={s.thCenter}>Outils</th>
							</tr>
						</thead>
						<tbody>
							{articleUsages.map((usage, i) => (
								<UsageRow
									key={usage.id}
									usage={usage}
									index={i}
									onAtelier={onAtelier}
									onCommande={onCommande}
								/>
							))}
						</tbody>
					</table>
				)}
			</div>

			{/* Sentinelle endless scroll + état bas de liste */}
			{articleUsages.length > 0 && (
				<div ref={sentinelRef} style={s.footer}>
					{articleUsagesLoadingMore ? (
						<FontAwesomeIcon icon={faSync} spin />
					) : !articleUsagesHasMore ? (
						"Fin de l'historique"
					) : null}
				</div>
			)}
		</>
	)
}

const s = {
	toolbar: {
		display: "flex",
		alignItems: "flex-end",
		gap: 16,
		marginBottom: 20,
		flexWrap: "wrap",
	},
	field: { display: "flex", flexDirection: "column" },
	label: {
		fontSize: 12,
		fontWeight: 600,
		color: COLORS.textMuted,
		marginBottom: 6,
	},
	input: {
		height: 38,
		minWidth: 200,
		padding: "0 12px",
		borderRadius: 10,
		border: `1px solid ${COLORS.border}`,
		fontSize: 14,
		color: COLORS.text,
		backgroundColor: COLORS.surface,
		fontFamily: "inherit",
		outline: "none",
	},
	thCenter: { ...tableStyles.th, textAlign: "center" },
	td: { ...tableStyles.td, padding: "11px 16px" },
	tdRight: { ...tableStyles.tdRight, padding: "11px 16px" },
	tdCenter: {
		...tableStyles.td,
		padding: "11px 16px",
		textAlign: "center",
		color: COLORS.textLight,
	},
	footer: {
		textAlign: "center",
		padding: "16px",
		color: COLORS.textLight,
		fontSize: 13,
	},
}

export default UtilisationsV2
